import { useEffect, useRef, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useStore } from '@/contexts/StoreContext';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { productService } from '@/lib/services/product.service';

/**
 * Link de produto com `?comprar=1` (o "Pagar no site" da /loja no jogo): põe
 * o produto no carrinho e vai para o carrinho. Sem login, abre o login antes
 * e continua depois de entrar. Vale para a página de produto de qualquer
 * template, porque envolve a rota.
 */
export function ComprarPeloLink({ children }: { children: ReactNode }) {
  const [params, setParams] = useSearchParams();
  const { serverId, productSlug } = useParams<{ serverId?: string; productSlug?: string }>();
  const { store } = useStore();
  const { isAuthenticated, openLoginModal } = useAuth();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const feito = useRef(false);
  const pediuLogin = useRef(false);

  useEffect(() => {
    if (params.get('comprar') !== '1' || !store?.id || !productSlug || feito.current) return;
    if (!isAuthenticated) {
      if (!pediuLogin.current) {
        pediuLogin.current = true;
        openLoginModal();
      }
      return;
    }
    feito.current = true;
    const semComprar = new URLSearchParams(params);
    semComprar.delete('comprar');
    setParams(semComprar, { replace: true });
    void productService
      .getBySlug(store.id, productSlug, serverId)
      .then(async (produto) => {
        if (!produto) return;
        await addItem(produto.id, Math.max(1, produto.minQuantity ?? 1));
        navigate('/carrinho');
      })
      .catch(() => undefined);
  }, [params, setParams, store?.id, productSlug, serverId, isAuthenticated, openLoginModal, addItem, navigate]);

  return <>{children}</>;
}
